import { useCallback } from 'react';
import {
  buildTolerantProjectParameterMutationBatches,
} from '../../revit/projectParameterMutationPlan';
import { mergeWithDefaultSharedParameters } from '../../revit/shippedSharedParameters';
import { buildDefinitionBackedParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { useSharedParameterStore } from '../../../stores/useSharedParameterStore';
import { fetchAvailableProjectParameters } from './availability';
import { rememberAvailableProjectParameters } from './availableProjectParameterCache';
import { MAPPED_PARAMETER_PROJECT_CATEGORIES } from './connectorPlan';
import { notifyParameterAddition } from './feedback';
import {
  adoptExistingMappedProjectParameterBindings,
  buildMappedProjectParameterBindingGroups,
  enforceMappedProjectParameterBindingGroups,
} from './mappedProjectParameterModePolicy';
import { projectChangedResult } from './operationIdentity';
import { type ParameterAdditionOptions, type ParameterAdditionResult } from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';

export function useMappedProjectParameterAddition(
  core: ParameterAdditionCore,
  mutations: ParameterMutationActions,
) {
  const {
    connectorConfig,
    currentProjectUndoKey,
    didProjectChange,
    setIsAdding,
    showError,
    showWarning,
    startParameterProgress,
  } = core;
  const { addAsProjectParameters } = mutations;

  const addMappedProjectParameters = useCallback(async (
    parameterNames: string[],
    options: ParameterAdditionOptions = {},
  ): Promise<ParameterAdditionResult> => {
    const params = sanitizeRevitParameterNames(parameterNames);
    if (params.length === 0) {
      return { success: true, addedCount: 0, requestedCount: 0 };
    }

    const requestedProjectKey = currentProjectUndoKey;
    if (!requestedProjectKey) {
      const error = 'Active Revit project identity is not available yet. Recheck this step before continuing.';
      notifyParameterAddition(options, showWarning, error);
      return { success: false, addedCount: 0, error };
    }

    setIsAdding(true);
    startParameterProgress({
      title: 'Adding mapped parameters',
      message: 'Revit is adding missing mapped parameters',
      detail: 'Parameters from the shared parameter file are bound to the piping categories.',
    });
    try {
      if (didProjectChange(requestedProjectKey)) {
        const result = projectChangedResult();
        notifyParameterAddition(
          options,
          showWarning,
          result.error ?? 'The active Revit project changed.',
        );
        return result;
      }

      const availableBeforeDescriptors = await fetchAvailableProjectParameters({
        cacheScopeKey: requestedProjectKey,
        force: true,
      });
      if (didProjectChange(requestedProjectKey)) {
        return projectChangedResult();
      }

      const parameterTypeHints = buildDefinitionBackedParameterTypeHints(
        params,
        mergeWithDefaultSharedParameters(
          useSharedParameterStore.getState().parsedParameters,
        ),
      );
      const mutationBatches = enforceMappedProjectParameterBindingGroups(
        adoptExistingMappedProjectParameterBindings(
          buildMappedProjectParameterBindingGroups({
            parameterNames: params,
            preferredBindingKind: connectorConfig.parameterBindingKind,
          }),
          availableBeforeDescriptors,
          parameterTypeHints,
        ).flatMap((group) =>
          buildTolerantProjectParameterMutationBatches(
            group.parameterNames,
            group.bindingKind,
            MAPPED_PARAMETER_PROJECT_CATEGORIES,
            availableBeforeDescriptors,
            parameterTypeHints,
          )
        )
      );
      if (mutationBatches.length === 0) {
        return {
          success: true,
          addedCount: 0,
          existingCount: params.length,
          requestedCount: params.length,
        };
      }

      const results: ParameterAdditionResult[] = [];
      for (const batch of mutationBatches) {
        const result = await addAsProjectParameters(batch.parameterNames, {
          categories: MAPPED_PARAMETER_PROJECT_CATEGORIES,
          bindingKind: batch.bindingKind,
          bindingConflictMode: 'treatAsExisting',
          parameterTypeHints,
        });
        if (didProjectChange(requestedProjectKey)) {
          return projectChangedResult();
        }
        if (!result.success) {
          return { ...result, requestedCount: params.length };
        }
        if (
          (result.failedCount || 0) === 0 &&
          (result.conflictingCount || 0) === 0 &&
          (result.bindingConflictExistingCount || 0) === 0
        ) {
          rememberAvailableProjectParameters(
            batch.parameterNames,
            batch.bindingKind,
            requestedProjectKey,
            MAPPED_PARAMETER_PROJECT_CATEGORIES,
            parameterTypeHints,
          );
        }
        results.push(result);
      }

      const sum = (field: keyof ParameterAdditionResult) => results.reduce(
        (total, result) => total + (typeof result[field] === 'number' ? result[field] : 0),
        0,
      );
      const mutatedCount = mutationBatches.reduce(
        (total, batch) => total + batch.parameterNames.length,
        0,
      );
      return {
        success: true,
        addedCount: sum('addedCount'),
        updatedCount: sum('updatedCount'),
        existingCount: params.length - mutatedCount + sum('existingCount'),
        failedCount: sum('failedCount'),
        conflictingCount: sum('conflictingCount'),
        bindingConflictExistingCount: sum('bindingConflictExistingCount'),
        queued: results.some((result) => result.queued),
        requestedCount: params.length,
        failedParameters: results.flatMap((result) => result.failedParameters ?? []),
        conflictingParameters: results.flatMap((result) => result.conflictingParameters ?? []),
        failureMessages: results.flatMap((result) => result.failureMessages ?? []),
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      notifyParameterAddition(
        options,
        showError,
        `Failed to add mapped project parameters: ${message}`,
      );
      return { success: false, addedCount: 0, error: message };
    } finally {
      setIsAdding(false);
    }
  }, [
    addAsProjectParameters,
    connectorConfig.parameterBindingKind,
    currentProjectUndoKey,
    didProjectChange,
    setIsAdding,
    showError,
    showWarning,
    startParameterProgress,
  ]);

  return addMappedProjectParameters;
}
